import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Pencil, Shield, AlertCircle, UserPlus } from 'lucide-react';
import { useOrg } from '../context/OrgContext';
import UserAvatar from './UserAvatar';

const PRIMARY_ROLES = [
  { value: 'HDO',              label: 'Head of Design Org. (HDO)' },
  { value: 'ChiefEng',         label: 'Chief Engineer' },
  { value: 'Structures',       label: 'Structures Lead' },
  { value: 'ConfigCompliance', label: 'Configuration & Compliance' },
  { value: 'IndepMonVerif',    label: 'Independent Monitoring & Verification' },
  { value: 'ExternalCVE',      label: 'External CVE' },
];

const ROLE_TINT = {
  'HDO':              'role-hdo',
  'ChiefEng':         'role-chief',
  'Structures':       'role-struct',
  'ConfigCompliance': 'role-config',
  'IndepMonVerif':    'role-verif',
  'ExternalCVE':      'role-cve',
};

function initials(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function PeopleAdmin({ user }) {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);  // Person being edited
  const [enrolling, setEnrolling] = useState(false);  // Enrol modal open?
  const [permissionError, setPermissionError] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get('/people');
      setPersons(res.data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setPermissionError(true);
        setError(err.response.data?.error || 'You don\'t have access to this section.');
      } else {
        setError(err.response?.data?.error || 'Failed to load people.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpdated = (updated) => {
    setEditing(null);
    setPersons(prev => prev.map(p => p._id === updated._id ? { ...p, ...updated } : p));
  };

  const handleEnrolled = (newPerson) => {
    setEnrolling(false);
    // Re-fetch so we get the populated User + sorted ordering
    fetchAll();
  };

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '60px 0' }}>
      <div className="loading-spinner"></div>
    </div>
  );

  if (permissionError) return (
    <div className="dasa-perm-error">
      <AlertCircle size={28} strokeWidth={1.5} />
      <h3>Not enrolled in DAS</h3>
      <p>{error}</p>
      <p className="dasa-perm-hint">
        Ask the HDO to enrol you as a Person in this organisation if you should have access.
      </p>
    </div>
  );

  return (
    <div className="dasa-section">
      {error && <div className="error-message">{error}</div>}

      <header className="dasa-section-header">
        <h2>{persons.length} {persons.length === 1 ? 'person' : 'people'} enrolled</h2>
        <button className="dt-btn-primary" onClick={() => setEnrolling(true)} style={{ padding: '7px 14px', fontSize: '12.5px' }}>
          <UserPlus size={14} /> Enrol person
        </button>
      </header>

      <div className="dasa-table">
        <div className="dasa-table-header">
          <span>Name</span>
          <span>Primary role</span>
          <span>CVE code</span>
          <span>Member of marks</span>
          <span></span>
        </div>
        {persons.map(person => {
          const u = person.user || {};
          const markCount = person.competenceMarks?.length || 0;
          const isCurrentUser = String(u._id) === String(user._id);
          return (
            <div key={person._id} className="dasa-table-row">
              <span className="dasa-name-cell">
                <UserAvatar user={u} className={`dasa-avatar ${isCurrentUser ? 'is-you' : ''}`} isYou={isCurrentUser} />
                <span className="dasa-name-block">
                  <span className="dasa-name">
                    {u.name || u.email || '—'}
                    {isCurrentUser && <span className="dasa-you-tag"> · you</span>}
                  </span>
                  <span className="dasa-email">{u.email}</span>
                </span>
              </span>
              <span className={`dasa-role-pill ${ROLE_TINT[person.primaryRole] || ''}`}>
                {person.primaryRole}
              </span>
              <span className="dasa-cve">
                {person.cveCode || <em className="dasa-muted">—</em>}
              </span>
              <span className="dasa-mark-count">
                {markCount} {markCount === 1 ? 'mark' : 'marks'}
              </span>
              <span className="dasa-actions">
                <button
                  className="dasa-btn-ghost dasa-btn-icon"
                  onClick={() => setEditing(person)}
                  title="Edit DAS role and CVE info"
                >
                  <Pencil size={13} />
                </button>
              </span>
            </div>
          );
        })}
      </div>

      <p className="dasa-foot-note">
        <Shield size={11} /> Only the HDO can edit DAS-layer role assignments or enrol new persons.
        New users join the org via the Settings invite flow; once they're org members the HDO enrols them here as DAS Persons.
      </p>

      {editing && (
        <EditPersonModal
          person={editing}
          onClose={() => setEditing(null)}
          onUpdated={handleUpdated}
        />
      )}

      {enrolling && (
        <EnrolPersonModal
          existingPersons={persons}
          onClose={() => setEnrolling(false)}
          onEnrolled={handleEnrolled}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// EditPersonModal — change primaryRole / cveCode / cveScope
// ─────────────────────────────────────────────────────────────────

function EditPersonModal({ person, onClose, onUpdated }) {
  const [primaryRole, setPrimaryRole] = useState(person.primaryRole);
  const [cveCode, setCveCode] = useState(person.cveCode || '');
  const [cveScope, setCveScope] = useState(person.cveScope || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const body = { primaryRole };
      // Only send CVE fields if the role is ExternalCVE (otherwise clear them)
      if (primaryRole === 'ExternalCVE') {
        body.cveCode = cveCode.trim() || null;
        body.cveScope = cveScope.trim();
      } else {
        body.cveCode = null;
        body.cveScope = '';
      }
      const res = await axios.put(`/people/${person._id}`, body);
      onUpdated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update person.');
      setSubmitting(false);
    }
  };

  const u = person.user || {};

  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">Edit DAS person</p>
            <h2>{u.name || u.email}</h2>
            <p className="dasa-modal-sub">{u.email}</p>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form">
          <div className="dt-field">
            <label htmlFor="ep-role">Primary role *</label>
            <select id="ep-role" value={primaryRole} onChange={e => setPrimaryRole(e.target.value)}>
              {PRIMARY_ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <span className="dt-field-hint">
              The Competence Matrix marks ({person.competenceMarks?.length || 0} current) are not affected by this change.
              Edit the marks directly in the Competence Matrix tab.
            </span>
          </div>

          {primaryRole === 'ExternalCVE' && (
            <>
              <div className="dt-field">
                <label htmlFor="ep-cve-code">CVE code</label>
                <input
                  id="ep-cve-code"
                  type="text"
                  value={cveCode}
                  onChange={e => setCveCode(e.target.value)}
                  placeholder="e.g. CVE-A, CVE-B, CVE-C"
                />
                <span className="dt-field-hint">
                  Per Annex C of the DOH. Optional but recommended for tracking.
                </span>
              </div>
              <div className="dt-field">
                <label htmlFor="ep-cve-scope">CVE scope</label>
                <textarea
                  id="ep-cve-scope"
                  rows={2}
                  value={cveScope}
                  onChange={e => setCveScope(e.target.value)}
                  placeholder="e.g. Static structural analysis verification (primary structure)"
                />
              </div>
            </>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="dt-btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// EnrolPersonModal — pick an existing org member and enrol them as
// a DAS Person. Calls POST /api/people.
// ─────────────────────────────────────────────────────────────────

function EnrolPersonModal({ existingPersons, onClose, onEnrolled }) {
  const { currentOrg } = useOrg();
  const [memberships, setMemberships] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberError, setMemberError] = useState('');

  const [userId, setUserId] = useState('');
  const [primaryRole, setPrimaryRole] = useState('ChiefEng');
  const [cveCode, setCveCode] = useState('');
  const [cveScope, setCveScope] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Set of already-enrolled user IDs (so we hide them from the picker)
  const enrolledUserIds = new Set(existingPersons.map(p => String(p.user?._id)).filter(Boolean));

  useEffect(() => {
    if (!currentOrg?._id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingMembers(true);
        const res = await axios.get(`/orgs/${currentOrg._id}/members`);
        if (cancelled) return;
        setMemberships(res.data || []);
      } catch (err) {
        if (cancelled) return;
        setMemberError(err.response?.data?.error || 'Failed to load org members.');
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentOrg]);

  const eligibleMembers = memberships.filter(m => !enrolledUserIds.has(String(m.user?._id)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!userId) { setError('Pick a person from the dropdown.'); return; }

    setSubmitting(true);
    try {
      const body = { userId, primaryRole };
      if (primaryRole === 'ExternalCVE') {
        if (cveCode.trim())  body.cveCode  = cveCode.trim();
        if (cveScope.trim()) body.cveScope = cveScope.trim();
      }
      const res = await axios.post('/people', body);
      onEnrolled(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enrol person.');
      setSubmitting(false);
    }
  };

  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">Enrol DAS person</p>
            <h2>Add to Design Assurance team</h2>
            <p className="dasa-modal-sub">
              Pick an existing org member and assign their procedural role.
            </p>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form">

          <div className="dt-field">
            <label htmlFor="ep-user">Org member *</label>
            {loadingMembers ? (
              <div className="loading-spinner" style={{ width: 18, height: 18, margin: '8px 0' }}></div>
            ) : memberError ? (
              <div className="error-message">{memberError}</div>
            ) : eligibleMembers.length === 0 ? (
              <p className="dt-field-hint" style={{ padding: '10px 0' }}>
                Every org member is already enrolled. To add someone new, invite them from
                Org Settings first.
              </p>
            ) : (
              <select id="ep-user" value={userId} onChange={e => setUserId(e.target.value)} required>
                <option value="">Select a person…</option>
                {eligibleMembers.map(m => (
                  <option key={m.user._id} value={m.user._id}>
                    {m.user.name || m.user.email} ({m.user.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="dt-field">
            <label htmlFor="ep-role">Primary role *</label>
            <select id="ep-role" value={primaryRole} onChange={e => setPrimaryRole(e.target.value)}>
              {PRIMARY_ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <span className="dt-field-hint">
              Determines which role this person plays in the procedure. Competence Matrix marks
              are set separately in the Competence tab.
            </span>
          </div>

          {primaryRole === 'ExternalCVE' && (
            <>
              <div className="dt-field">
                <label htmlFor="ep-cve-code">CVE code</label>
                <input
                  id="ep-cve-code"
                  type="text"
                  value={cveCode}
                  onChange={e => setCveCode(e.target.value)}
                  placeholder="e.g. CVE-A, CVE-B, CVE-C"
                />
              </div>
              <div className="dt-field">
                <label htmlFor="ep-cve-scope">CVE scope</label>
                <textarea
                  id="ep-cve-scope"
                  rows={2}
                  value={cveScope}
                  onChange={e => setCveScope(e.target.value)}
                  placeholder="e.g. Static structural analysis verification (primary structure)"
                />
              </div>
            </>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="dt-btn-primary"
              disabled={submitting || loadingMembers || eligibleMembers.length === 0}
            >
              {submitting ? 'Enrolling…' : 'Enrol person'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PeopleAdmin;
